import React, { useEffect, useState } from "react";
import { ExclamationTriangleIcon, DocumentArrowDownIcon } from "@heroicons/react/24/outline";

const TONES = {
  primary: {
    ring: "bg-blue-100",
    icon: "text-blue-600",
    button: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
  },
  warning: {
    ring: "bg-amber-100",
    icon: "text-amber-600",
    button: "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500",
  },
};

/**
 * Centred confirmation modal, used instead of window.confirm.
 *
 * Beyond looking better, this keeps the browser's own dialog out of the way:
 * window.confirm ends the transient user activation that the File System Access
 * API needs, so confirming through it made the following permission request
 * fail. A click on this dialog's confirm button is a fresh activation.
 */
const ConfirmDialog = ({
  open,
  title,
  lines = [],
  note,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "primary",
  rememberLabel,
  onConfirm,
  onCancel,
}) => {
  const [remember, setRemember] = useState(false);

  useEffect(() => {
    if (open) setRemember(false); // each new dialog starts unticked
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onCancel]);

  if (!open) return null;

  const palette = TONES[tone] || TONES.primary;
  const Icon = tone === "warning" ? ExclamationTriangleIcon : DocumentArrowDownIcon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm"
        onClick={onCancel}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden"
      >
        <div className="px-6 pt-6 pb-4 text-center">
          <div
            className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${palette.ring}`}
          >
            <Icon className={`h-7 w-7 ${palette.icon}`} />
          </div>

          <h3 className="mt-4 text-lg font-semibold text-gray-900">{title}</h3>

          {/* Left-aligned: centred multi-line sentences read as a ragged block. The list
              lines up with the note below, while the icon and title stay centred. */}
          <ul className="mt-3 space-y-2 text-left list-disc pl-8 pr-3">
            {lines.map((line, index) => (
              <li key={index} className="text-sm text-gray-600 leading-relaxed">
                {line}
              </li>
            ))}
          </ul>

          {note && (
            <p className="mt-4 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-md p-3 text-left leading-relaxed">
              {note}
            </p>
          )}
        </div>

        {rememberLabel && (
          <div className="px-6 pb-2 flex justify-center">
            <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={remember}
                onChange={(event) => setRemember(event.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              {rememberLabel}
            </label>
          </div>
        )}

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            autoFocus
            onClick={() => onConfirm({ remember })}
            className={`px-5 py-2 text-sm font-medium rounded-md text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${palette.button}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
